/**
 * drawingEngine: Canvas 2D rendering primitives.
 *
 * M2.T4 + M2.T6 per IMPLEMENTATION_PLAN §7 M2.T4/T6 + PRD §6.1.
 *
 * Handles:
 *  - Drawing a smoothed stroke from a SVG path string.
 *  - Eraser strokes by painting the opaque white canvas surface (source-over),
 *    NOT `destination-out`: see v4 T2 below.
 *  - Clearing the full canvas to an opaque white base.
 *
 * v4 T2 (eraser/fill compositing): the committed bitmap is an OPAQUE surface and
 * the eraser "paints the surface colour" instead of punching transparent holes.
 * This makes erased pixels distinct from never-drawn pixels at the raster level,
 * so the flood-fill bucket can be bounded by an eraser stroke (and a fill into
 * an erased pocket no longer leaks across the whole transparent plane).
 * Crucially this is a RENDERER property keyed off `isEraser`, the stored op log
 * and wire bytes are unchanged (eraser strokes still carry `isEraser:true` +
 * colorIdx=ERASER), so old and new snapshots replay identically and no
 * PROTOCOL_VERSION bump is needed.
 *
 * UI-batch (item 4): the canvas DRAWING SURFACE is now WHITE (#ffffff), not the
 * cream `--paper` page background. The opaque bitmap fill + the eraser paint
 * white so the drawing area reads white and DISTINCT from the cream page, for
 * both Free Draw and in-game. (The constant is still named `PAPER` for its role
 * as the canvas-surface colour; it just no longer equals the `--paper` token.)
 *
 * Coordinates use the shared 1600 by 1200 drawing space.
 * The committed bitmap uses scale 1. The visible bitmap uses its physical
 * width divided by 1600, so every screen shows the same whole picture.
 *
 * M5 fix: brush size is passed as `size` to strokeToSvgPath() so
 * perfect-freehand embeds the width in the outline geometry. Using
 * ctx.scale() for brush width scaling is wrong, it scales around the
 * canvas origin and misplaces the stroke geometry.
 */

import type { BrandColor } from "@drawlol/shared";
import type { BrushSizeIndex } from "../tools/toolStore.js";
import { BRUSH_SIZE_PX } from "../tools/toolStore.js";
import { strokeToSvgPath } from "./strokeToSvgPath.js";
import { effectiveBrushWidth } from "./pressure.js";
import type { StrokePoint } from "./strokeToSvgPath.js";

/**
 * Opaque WHITE drawing-surface colour for the canvas (UI-batch item 4). The
 * eraser paints this colour and `clearCanvas` fills it, so the committed bitmap
 * is a fully-opaque white surface the flood-fill can reason about, and the
 * drawing area reads WHITE, distinct from the cream `--paper` page background.
 *
 * NOTE: unlike v4 T2 (where this equalled the `--paper` token), the canvas
 * surface is now intentionally white and DIFFERENT from `--paper`, the
 * cssContract test asserts `PAPER === #ffffff` and `PAPER !== --paper`.
 */
export const PAPER = "#ffffff";

/**
 * Applies the drawing-space scale to a 2D context after canvas resize.
 */
export function applyDprTransform(ctx: CanvasRenderingContext2D, dpr: number): void {
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}

/**
 * Draw a smoothed stroke path onto the canvas.
 *
 * @param ctx       2D context (already DPR-scaled via applyDprTransform)
 * @param points    Raw stroke samples in logical coordinates
 * @param color     BrandColor hex string for brush; ignored for eraser
 * @param sizeIdx   Brush size index (0=thin, 1=medium, 2=thick)
 * @param isEraser  When true, paints the opaque white surface (source-over) so
 *                  erased pixels are real surface, not transparent holes (v4 T2)
 * @param pointerType  Used for pressure modulation
 * @param lastPressure Pressure of the last sample (for width modulation)
 */
export function drawStrokePath(
  ctx: CanvasRenderingContext2D,
  points: StrokePoint[],
  color: BrandColor,
  sizeIdx: BrushSizeIndex,
  isEraser: boolean,
  pointerType: string = "mouse",
  lastPressure: number = 0,
): void {
  if (points.length === 0) return;

  const baseWidth = BRUSH_SIZE_PX[sizeIdx];
  // effectiveBrushWidth applies pressure modulation (pen/stylus).
  const width = effectiveBrushWidth(baseWidth, pointerType, lastPressure);

  // Pass `size` to strokeToSvgPath so perfect-freehand bakes the brush width
  // directly into the outline geometry. No ctx.scale() needed, that approach
  // scaled around the canvas origin and displaced the entire stroke (M5 fix).
  const pathStr = strokeToSvgPath(points, width);
  if (!pathStr) return;

  ctx.save();

  // v4 T2: the eraser paints the opaque white surface (source-over) rather than
  // punching a transparent hole (destination-out). Same visual result over the
  // white base, but the erased pixels are now real surface, so the flood-fill
  // bucket treats them as a fillable region bounded by ink, not the transparent void.
  ctx.globalCompositeOperation = "source-over";
  ctx.fillStyle = isEraser ? PAPER : color;

  const path = new Path2D(pathStr);
  ctx.fill(path);
  ctx.restore();
}

/**
 * Draw a stroke from a finalised SVG path string (audience replay / reconnect sync).
 *
 * The SVG path was already produced by strokeToSvgPath() with the correct `size`
 * baked in by perfect-freehand, so no additional scaling is needed here.
 */
export function drawFinalStroke(
  ctx: CanvasRenderingContext2D,
  svgPath: string,
  color: BrandColor,
  _sizeIdx: BrushSizeIndex,
  isEraser: boolean,
): void {
  if (!svgPath) return;

  ctx.save();

  // v4 T2: eraser paints the opaque white surface (source-over), matching drawStrokePath.
  ctx.globalCompositeOperation = "source-over";
  ctx.fillStyle = isEraser ? PAPER : color;

  // No ctx.scale(): the SVG path geometry already encodes the correct width
  // (M5 fix: the upstream strokeToSvgPath call passes size to perfect-freehand).
  const path = new Path2D(svgPath);
  ctx.fill(path);
  ctx.restore();
}

/**
 * Reset the entire canvas to the opaque white `PAPER` surface (v4 T2). Previously
 * this cleared to transparent and relied on a CSS background showing through; now
 * the bitmap itself carries the white surface so the flood-fill bucket has a real,
 * opaque surface to flood and erased pixels are surface rather than the
 * transparent void. After painting the surface with the identity transform,
 * re-apply the DPR scale so subsequent draws keep using CSS-pixel coordinates.
 *
 * The `dpr` argument is REQUIRED so we can re-apply the exact same scale
 * `applyDprTransform()` used on the most recent resize. Prior versions
 * inferred dpr from `ctx.canvas.width / 1600`, which broke the moment the
 * canvas was sized for anything other than a 1600-wide CSS box.
 */
export function clearCanvas(ctx: CanvasRenderingContext2D, dpr: number = 1): void {
  ctx.save();
  ctx.globalCompositeOperation = "source-over";
  // Reset transform to identity so the paper fill covers the full backing store.
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.fillStyle = PAPER;
  ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  ctx.restore();
  // Restore the DPR scale so callers continue to draw in CSS pixels.
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}
